import { useCallback, useEffect, useRef, useState } from 'react';

import { onPanelHidden } from '../panelVisibility';
import { audioRuns, connectCapture, openMicrophone } from './microphoneCapture';
import {
    base64ToPcm16,
    floatToPcm16,
    FrameBatcher,
    levelOf,
    PlaybackOutput,
    PlaybackQueue,
    pcm16ToBase64,
    pcm16ToFloat,
    playStartTone,
    Resampler,
    VOICE_SAMPLE_RATE,
} from './voiceAudio';
import { VoiceActivity } from './voiceActivity';
import { MAX_RECONNECT_LATENESS_MS, ReconnectPolicy } from './voiceReconnect';
import { createVoiceHubConnection, VoiceServerEvent, VoiceSessionClient } from './VoiceSessionClient';

export type VoiceState = 'idle' | 'connecting' | 'reconnecting' | 'listening' | 'thinking' | 'speaking';

export interface UseVoiceSessionOptions {
    /** The chat the conversation belongs to; the session ends when this changes. */
    chatId: string;
    backendUrl: string;
    providerName: string;
    getToken: () => string | undefined;
    getFreshToken: () => Promise<string | undefined>;
    /**
     * Why the conversation ended (the server's end reason, `voice_unavailable`, `connect_failed`, `microphone` or
     * `audio_blocked`). Not called for an end it reconnects after, only for the last one. `superseded` also when a
     * start in another window called off a reconnect.
     */
    onEnded: (reason: string) => void;
    /** Why the server cancelled one question's answer (`response.cancelled`); the session goes on. */
    onCancelled: (reason: string) => void;
    /**
     * Send silence instead of the microphone while an answer or the start chime plays, and for a moment after: with
     * loudspeakers the microphone picks the answer up again, and the server takes it as the user talking over it (the
     * answer stops) or as a new question. The price is that the user cannot interrupt by speaking. Read at every chunk.
     */
    muteMicWhileSpeaking?: boolean;
}

/**
 * How long the microphone stays silenced after the last answer audio is due to end: the room's echo dies away, and the
 * audio reaches the speakers later than the output clock says (a Bluetooth or HDMI path adds more than it reports).
 */
export const MUTE_TAIL_SECONDS = 0.5;

/**
 * The same for the start chime, shorter: the chime is the user's cue to talk, and a question answered at once must keep
 * its first word. It is quiet and dies away within its 130 ms, so this mostly covers the speakers' latency.
 */
export const START_TONE_TAIL_SECONDS = 0.15;

interface Running {
    client: VoiceSessionClient;
    stream: MediaStream;
    capture: AudioContext;
    playback: AudioContext;
    processor: AudioWorkletNode;
    source: MediaStreamAudioSourceNode;
    queue: PlaybackQueue;
    timer: ReturnType<typeof setInterval>;
}

const webAudioOutput = (context: AudioContext): PlaybackOutput => ({
    get currentTime() {
        return context.currentTime;
    },
    play(samples, startAt) {
        const buffer = context.createBuffer(1, samples.length, VOICE_SAMPLE_RATE);
        buffer.getChannelData(0).set(samples);
        const node = context.createBufferSource();
        node.buffer = buffer;
        node.connect(context.destination);
        node.start(startAt);
        return {
            duration: buffer.duration,
            stop: () => {
                try {
                    node.stop();
                } catch {
                    // Already stopped or never started.
                }
                node.disconnect();
            },
        };
    },
});

// A conversation that has had a session come up, so an end it did not ask for may reconnect.
interface Conversation {
    policy: ReconnectPolicy;
    chatId: string;
    hostContextJson: string;
    // From its first session.ready, for the server to refuse a reconnect once the user has started a newer
    // conversation, on another device for instance.
    startedAt: string;
    // Set while it waits for its next attempt; nothing else runs then.
    retry?: ReturnType<typeof setTimeout>;
    // From a dropped session until a new one is ready.
    reconnecting?: boolean;
}

// The server keeps one voice session per user and the newest wins, so a reconnect that is still on its way in one
// window would take over the session the user has just started in another. A start says so to the other windows once
// it has the microphone and its audio, so they call theirs off; the server refuses one that gets through
// (`reconnect_superseded`). Not before: a start that then fails would leave no session anywhere.
const VOICE_CHANNEL = 'aia-voice';
const VOICE_STARTED = 'voice.started';

const openVoiceChannel = (): BroadcastChannel | undefined => {
    try {
        return new BroadcastChannel(VOICE_CHANNEL);
    } catch {
        // Not supported (jsdom has none either): voice works on, only without calling off other windows' reconnects.
        return undefined;
    }
};

interface AudioContexts {
    capture: AudioContext;
    playback: AudioContext;
}

// Made in the click, before the microphone prompt: made after it, they may no longer count the click, and Safari/iOS
// then keeps them suspended until the user clicks again. A reconnect makes them from a timer either way.
// Either can throw (an unsupported sample rate). Each goes into `contexts` as soon as it exists, so the caller can close
// what was made.
const createAudioContexts = (contexts: AudioContext[]): AudioContexts => {
    const capture = new AudioContext();
    contexts.push(capture);
    const playback = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE });
    contexts.push(playback);
    // Not awaited: a resume that never settles must not hang the start.
    contexts.forEach((context) => {
        context.resume().catch(() => undefined);
    });
    return { capture, playback };
};

/** One voice conversation in the selected chat: microphone → /voiceHub → spoken answer. */
export const useVoiceSession = (options: UseVoiceSessionOptions) => {
    const [state, setState] = useState<VoiceState>('idle');
    const [level, setLevel] = useState(0);
    // Whether the assistant owes an answer. Unlike `thinking`, it holds while the microphone picks up a sound.
    const [waiting, setWaiting] = useState(false);
    // The questions heard, for the button to say "thinking" once per question. Here, not in VoiceActivity: that starts
    // over with each session, and a count that did too would give a reconnect's first question an earlier one's number.
    const [question, setQuestion] = useState(0);
    const running = useRef<Running | null>(null);
    // The start still waiting for the microphone or the worklet. Set synchronously on the click, before any
    // await: `running` only exists once both are up, and a guard on it alone let rapid clicks open several
    // sessions (the PoC's "three voices at once"). Each start gets its own controller, so a stop during the
    // browser's microphone prompt, which can stay open indefinitely, ends that start at once. The
    // abandoned start releases its own microphone whenever the prompt is answered, and a new click
    // need not wait for that.
    const pending = useRef<AbortController | null>(null);
    // A session the user already stopped still ends later, when a new one may be running.
    const isRunning = (client: VoiceSessionClient) => running.current?.client === client;
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const levelRef = useRef(0);
    const conversation = useRef<Conversation | null>(null);
    // Through a ref: a reconnect opens a session from a timer, and `open` decides about reconnects itself.
    const openRef = useRef<(chatId: string, hostContextJson: string, isReconnect: boolean) => Promise<void>>();
    const otherWindows = useRef<BroadcastChannel>();

    const teardown = useCallback(() => {
        const r = running.current;
        running.current = null;
        if (!r) {
            return;
        }
        clearInterval(r.timer);
        r.processor.port.onmessage = null;
        r.source.disconnect();
        r.processor.disconnect();
        r.stream.getTracks().forEach((track) => {
            track.stop();
        });
        r.queue.flush();
        void r.capture.close();
        void r.playback.close();
        setState('idle');
        setLevel(0);
        setWaiting(false);
    }, []);

    const stop = useCallback(() => {
        // The user's decision, so nothing reconnects after it.
        const waiting = conversation.current?.retry;
        conversation.current = null;
        if (waiting !== undefined) {
            clearTimeout(waiting);
            setState('idle');
            return;
        }
        if (pending.current) {
            pending.current.abort();
            pending.current = null;
            setState('idle');
            return;
        }
        const client = running.current?.client;
        // Silence the answer and release the microphone now, not when the server's `ended` arrives:
        // that waits for the server's drain, up to 15 s.
        teardown();
        client?.stop();
    }, [teardown]);

    // Every end the user did not ask for comes here, once its session is torn down. The new session starts clean:
    // the microphone is released while it waits, so no audio is kept to be replayed.
    const ended = useCallback((reason: string) => {
        const current = conversation.current;
        const delay = current?.policy.nextDelay(reason, Date.now());
        // No token means the user has logged out, and the hub would refuse the attempt anyway.
        if (current && delay !== undefined && optionsRef.current.getToken()) {
            setState('reconnecting');
            current.reconnecting = true;
            // Wall-clock time, as the server's memory of the user's conversations is: a sleeping laptop's monotonic
            // clock may stand still.
            const dueAt = Date.now() + delay;
            current.retry = setTimeout(() => {
                current.retry = undefined;
                // Again when it is due: the token can go while it waits, and the microphone must not open for a hub
                // that will refuse it. Nor for an attempt that comes due long after it was planned.
                if (!optionsRef.current.getToken() || Date.now() - dueAt > MAX_RECONNECT_LATENESS_MS) {
                    conversation.current = null;
                    setState('idle');
                    optionsRef.current.onEnded(reason);
                    return;
                }
                void openRef.current?.(current.chatId, current.hostContextJson, true);
            }, delay);
            return;
        }
        conversation.current = null;
        optionsRef.current.onEnded(reason);
    }, []);

    const open = useCallback(
        async (sessionChatId: string, hostContextJson: string, isReconnect: boolean) => {
            if (running.current || pending.current) {
                return;
            }
            const attempt = new AbortController();
            pending.current = attempt;
            // Read through a function: a stop can abort during an await, which a second read of `aborted`
            // after an earlier check would look like dead code to the type checker.
            const isCancelled = () => attempt.signal.aborted;
            setState(isReconnect ? 'reconnecting' : 'connecting');

            const abandon = (stream?: MediaStream, contexts: AudioContext[] = []) => {
                stream?.getTracks().forEach((track) => {
                    track.stop();
                });
                contexts.forEach((context) => {
                    void context.close();
                });
                // A stopped start already went idle, and a newer start may be connecting by now.
                if (!isCancelled()) {
                    pending.current = null;
                    setState('idle');
                }
            };

            const contexts: AudioContext[] = [];
            let stream: MediaStream | undefined;
            let released = false;
            const release = () => {
                if (!released) {
                    released = true;
                    abandon(stream, contexts);
                }
            };
            // A stop must release what this start holds at once, not when the microphone prompt is answered or the
            // worklet has loaded: the button already reads idle, and each new click would otherwise open another capture.
            attempt.signal.addEventListener('abort', release);

            let audio: AudioContexts;
            try {
                audio = createAudioContexts(contexts);
                stream = await openMicrophone();
            } catch {
                release();
                // A refusal after the user stopped is no news to them, and may land in another chat.
                if (!isCancelled()) {
                    ended('microphone');
                }
                return;
            }
            if (isCancelled()) {
                // The stop released the rest before this microphone was granted.
                stream.getTracks().forEach((track) => {
                    track.stop();
                });
                return;
            }

            const { capture, playback } = audio;
            let capturing: { source: MediaStreamAudioSourceNode; processor: AudioWorkletNode };
            try {
                capturing = await connectCapture(capture, stream);
            } catch {
                release();
                if (!isCancelled()) {
                    ended('microphone');
                }
                return;
            }
            // A reconnect opens its audio from a timer, with no click, and the answer would also play to no one. Final,
            // and with a message that asks for a click: the next attempt would come from a timer as well.
            if (isReconnect && !(await audioRuns([capture, playback]))) {
                release();
                if (!isCancelled()) {
                    ended('audio_blocked');
                }
                return;
            }
            if (isCancelled()) {
                release();
                return;
            }
            attempt.signal.removeEventListener('abort', release);
            if (!isReconnect) {
                otherWindows.current?.postMessage(VOICE_STARTED);
            }
            const { source, processor } = capturing;

            const queue = new PlaybackQueue(webAudioOutput(playback));
            // When the last start chime ends, on the playback clock; it plays outside the queue.
            let toneEndsAt = Number.NEGATIVE_INFINITY;
            const resampler = new Resampler(capture.sampleRate);
            const batcher = new FrameBatcher();
            const activity = new VoiceActivity();

            const handleEvent = (event: VoiceServerEvent) => {
                activity.onEvent(event, Date.now());
                switch (event.t) {
                    case 'session.ready':
                        if (conversation.current) {
                            conversation.current.policy.onReady();
                            conversation.current.reconnecting = false;
                        } else if (event.conversationStartedAt !== undefined) {
                            // Without it the server cannot tell a reconnect from a new start, and the newest start
                            // wins: a reconnect would take over a conversation the user has started since. So a
                            // server that does not send it (an older backend) gets no reconnects.
                            conversation.current = {
                                policy: new ReconnectPolicy(),
                                chatId: sessionChatId,
                                hostContextJson,
                                startedAt: event.conversationStartedAt,
                            };
                        }
                        setState('listening');
                        toneEndsAt = playStartTone(playback);
                        break;
                    case 'speech.started':
                        // Barge-in: the user talks over the answer, so stop it now.
                        queue.flush();
                        setQuestion((heard) => heard + 1);
                        break;
                    case 'audio':
                        if (event.pcm) {
                            queue.enqueue(
                                event.turnId ?? event.responseId ?? 'answer',
                                pcm16ToFloat(base64ToPcm16(event.pcm)),
                            );
                        }
                        break;
                    case 'response.cancelled':
                        // Refused, unheard, …: stop exactly that question's audio, immediately.
                        if (event.turnId) {
                            queue.cancel(event.turnId);
                        } else {
                            queue.flush();
                        }
                        if (event.reason) {
                            optionsRef.current.onCancelled(event.reason);
                        }
                        break;
                    default:
                        break;
                }
            };

            const client = new VoiceSessionClient(
                createVoiceHubConnection(
                    optionsRef.current.backendUrl,
                    optionsRef.current.providerName,
                    optionsRef.current.getToken,
                ),
                {
                    onEvent: handleEvent,
                    onEnded: (reason) => {
                        if (!isRunning(client)) {
                            return;
                        }
                        teardown();
                        ended(reason);
                    },
                    getFreshToken: () => optionsRef.current.getFreshToken(),
                },
            );

            processor.port.onmessage = (e: MessageEvent<Float32Array>) => {
                // Zeros rather than nothing, so the server's audio stays continuous and simply hears no one.
                const outputLatency = Number.isFinite(playback.outputLatency) ? playback.outputLatency : 0;
                // Only on a running clock: a suspended or interrupted playback context stops its time, which would
                // otherwise keep the microphone muted after the first answer for good.
                const muted =
                    optionsRef.current.muteMicWhileSpeaking === true &&
                    playback.state === 'running' &&
                    playback.currentTime <
                        Math.max(queue.endsAt + MUTE_TAIL_SECONDS, toneEndsAt + START_TONE_TAIL_SECONDS) +
                            outputLatency;
                const input = muted ? new Float32Array(e.data.length) : e.data;
                levelRef.current = levelOf(input);
                activity.onLevel(levelRef.current, Date.now());
                if (!client.isReady) {
                    return;
                }
                for (const frame of batcher.push(floatToPcm16(resampler.process(input)))) {
                    client.sendAudio(pcm16ToBase64(frame));
                }
            };

            const timer = setInterval(() => {
                setLevel(levelRef.current);
                setWaiting(activity.waiting);
                setState((current) =>
                    current === 'connecting' || current === 'reconnecting' || current === 'idle'
                        ? current
                        : activity.state(queue.isPlaying, Date.now()),
                );
            }, 150);

            running.current = { client, stream, capture, playback, processor, source, queue, timer };
            pending.current = null;
            try {
                await client.start(
                    sessionChatId,
                    hostContextJson,
                    isReconnect ? conversation.current?.startedAt : undefined,
                );
            } catch {
                // The hub never came up. The browser hides a failed upgrade's HTTP status, so a refusal by the
                // start rate limit (429) looks the same as a network failure: say only that connecting failed.
                if (isRunning(client)) {
                    teardown();
                    ended('connect_failed');
                }
            }
        },
        [ended, teardown],
    );
    openRef.current = open;

    const start = useCallback(
        async (hostContextJson: string) => {
            if (running.current || pending.current || conversation.current?.retry !== undefined) {
                return;
            }
            // A new conversation: it may reconnect only once one of its own sessions has come up.
            conversation.current = null;
            await open(optionsRef.current.chatId, hostContextJson, false);
        },
        [open],
    );

    // A start elsewhere calls off a reconnect here, whether it is waiting, at the microphone or connecting. A session
    // that is up is left alone: the server ends it as superseded.
    useEffect(() => {
        const channel = openVoiceChannel();
        if (!channel) {
            return;
        }
        otherWindows.current = channel;
        channel.onmessage = (event: MessageEvent<unknown>) => {
            if (event.data === VOICE_STARTED && conversation.current?.reconnecting) {
                stop();
                optionsRef.current.onEnded('superseded');
            }
        };
        return () => {
            otherWindows.current = undefined;
            channel.close();
        };
    }, [stop]);

    // On unmount and on a chat change: "New chat" and the company picker swap the selected chat in place
    // without remounting the button, so the conversation would go on in a chat no longer on screen.
    const { chatId } = options;
    useEffect(() => stop, [chatId, stop]);
    // Closing the panel only hides it, so the microphone would stay open behind a closed assistant. A tab
    // switch is deliberately not a reason: the user may look something up mid-conversation.
    useEffect(() => onPanelHidden(stop), [stop]);

    return { state, level, waiting, question, start, stop };
};
